"use client"

import Image from "next/image"

import GameCard from "@/stories/molecules/cards/gameCard/GameCard"
import type { GameCardProps } from "@/stories/molecules/cards/gameCard/interface"

import { useGameSuggestions } from "../../hooks/useGameSuggestions"
import { usePopularGames } from "../../hooks/usePopularGames"
import { useRecommendedGames } from "../../hooks/useRecommendedGames"
import { formatSearchResults, searchTexts } from "../../localization/texts"

type SearchGame = {
  identifier: string | null
  title: string | null
  producer: string | null
  provider: string | null
  currency?: string | null
  thumb_url: string | null
  ai_thumb_url?: string | null
}

function toCardGame(game: SearchGame): GameCardProps["game"] | null {
  const identifier = game.identifier?.trim()
  const provider = game.provider?.trim()

  if (!identifier || !provider) {
    return null
  }

  return {
    identifier,
    title: game.title?.trim() || searchTexts.games,
    producer: game.producer?.trim() || "",
    provider,
    currency: game.currency,
    thumb_url: game.thumb_url ?? undefined,
    ai_thumb_url: game.ai_thumb_url
  }
}

function GameResultCard({ game }: { game: SearchGame }) {
  const card = toCardGame(game)

  if (!card) {
    return null
  }

  return (
    <li>
      <GameCard game={card} showCurrencyBadge={false} />
    </li>
  )
}

function RecommendedGames() {
  const { games, isLoading, isError } = useRecommendedGames()

  if (isLoading || isError || games.length === 0) {
    return null
  }

  return (
    <div className="flex flex-col gap-3 ls:hidden">
      <p className="px-2 text-sm text-white">{searchTexts.recommendedPrompt}</p>
      <ul className="grid grid-cols-3 gap-2 md:grid-cols-5 xlg:gap-4">
        {games.map((game, index) => {
          const label = game.title?.trim() || searchTexts.games
          const rowKey =
            game.identifier ??
            `${label}-${game.producer ?? "producer"}-${index}`

          return <GameResultCard key={rowKey} game={game} />
        })}
      </ul>
    </div>
  )
}

function PopularGames() {
  const { games, isLoading, isError } = usePopularGames()

  if (isLoading || isError || games.length === 0) {
    return null
  }

  return (
    <div className="flex flex-col gap-3 ls:hidden">
      <p className="px-2 text-sm text-white">{searchTexts.popularPrompt}</p>
      <ul className="grid grid-cols-3 gap-2 md:grid-cols-5 xlg:gap-4">
        {games.map((game, index) => {
          const label = game.title?.trim() || searchTexts.games
          const rowKey =
            game.identifier ??
            `${label}-${game.producer ?? "producer"}-${index}`

          return <GameResultCard key={rowKey} game={game} />
        })}
      </ul>
    </div>
  )
}

type GameResultsProps = {
  query: string
  showRecommended?: boolean
}

export function GameResults({
  query,
  showRecommended = false
}: GameResultsProps) {
  const trimmed = query.trim()
  const { suggestions, isLoading, isError } = useGameSuggestions(query)

  if (trimmed.length === 0) {
    return showRecommended ? <RecommendedGames /> : null
  }

  if (trimmed.length < 2) {
    return null
  }

  if (isLoading) {
    return (
      <p className="px-2 py-3 text-sm text-white/50">
        {searchTexts.gamesLoading}
      </p>
    )
  }

  if (isError) {
    return (
      <p className="px-2 py-3 text-sm text-white/50" role="alert">
        {searchTexts.gamesError}
      </p>
    )
  }

  if (suggestions.length === 0) {
    return (
      <div className="flex flex-col gap-3">
        <p className="px-2 pb-1 text-sm text-white">{formatSearchResults(0)}</p>
        <div className="flex flex-col items-center gap-3 px-2 py-6 text-center">
          <Image
            src="/img/search/rayo-empty.png"
            alt=""
            width={120}
            height={120}
            className="size-[120px] object-contain"
          />
          <p className="text-sm text-white/50">{searchTexts.gamesNone}</p>
        </div>
        <PopularGames />
      </div>
    )
  }

  return (
    <>
      <p className="px-2 pb-1 text-sm text-white">
        {formatSearchResults(suggestions.length)}
      </p>
      <ul className="grid grid-cols-3 gap-2 md:grid-cols-5 xlg:grid-cols-6 xlg:gap-4">
        {suggestions.map((game, index) => {
          const label = game.title?.trim() || searchTexts.games
          const rowKey =
            game.identifier ??
            `${label}-${game.producer ?? "producer"}-${index}`

          return <GameResultCard key={rowKey} game={game} />
        })}
      </ul>
    </>
  )
}
